package com.essntl.uikit.components.chat.message

import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.essntl.features.file.domain.model.FileModel
import com.essntl.uikit.components.file.AttachmentCard
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.icons.EsLargeIcon
import com.essntl.uikit.components.input.EsInput
import com.essntl.uikit.components.utils.spacing
import core.validation.EsInputState
import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

@OptIn(FlowPreview::class)
@Composable
fun EsSendMessageInput(
    value: String,
    onValueChange: (String) -> Unit,
    onSend: (String) -> Unit,
    label: String? = null,
    message: String? = null,
    showClearButton: Boolean = true,
    enabled: Boolean = true,
    readOnly: Boolean = !enabled,
    state: EsInputState = EsInputState.Default,
    visualTransformation: VisualTransformation = VisualTransformation.None,
    keyboardOptions: KeyboardOptions = KeyboardOptions.Default,
    keyboardActions: KeyboardActions = KeyboardActions.Default,
    minLines: Int = 1,
    interactionSource: MutableInteractionSource = remember { MutableInteractionSource() },
    cursorColor: Color = Color.Unspecified,
    minHeight: Dp = Dp.Unspecified,
    shape: Shape = RoundedCornerShape(12.dp),
    modifier: Modifier = Modifier,
) {
    var filesModel by remember { mutableStateOf<List<FileModel>>(emptyList()) }

    var scrollPosition by remember {
        mutableStateOf(0)
    }

    val lazyListState = rememberLazyListState(
        initialFirstVisibleItemIndex = scrollPosition,
    )

    LaunchedEffect(lazyListState) {
        snapshotFlow {
            lazyListState.firstVisibleItemIndex
        }.debounce(500L)
            .collectLatest { index ->
                scrollPosition = index
            }
    }

    val scope = rememberCoroutineScope()

//    val pickerLauncher = rememberFilePickerLauncher(
//        type = FilePickerFileType.All,
//        selectionMode = FilePickerSelectionMode.Multiple,
//        onResult = { files ->
//            if (files.isNotEmpty())
//                filesModel = filesModel + files.map {
//                    FileModel.empty.copy(
//                        url = it.getPath(context) ?: "",
//                        name = it.getName(context) ?: "",
//                    )
//                }
//        },
//    )

    Column {
        if (filesModel.isNotEmpty()) {
            Box(
                modifier = modifier,
                contentAlignment = Alignment.Center,
            ) {
                LazyRow(
                    horizontalArrangement = Arrangement.spacedBy(1.spacing),
                    state = lazyListState,
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    itemsIndexed(filesModel) { index, file ->
                        val uploadDate = file.createdAt
                            .ifEmpty {
                                Clock.System.now().toLocalDateTime(TimeZone.UTC).date.toString()
                            }

                        AttachmentCard(
                            file = file,
                            date = uploadDate,
                            onDelete = {
                                filesModel = filesModel.filterIndexed { i, _ ->
                                    index != i
                                }
                            },
                            modifier = Modifier.width(240.dp),
                        )

                    }
                }

                if (lazyListState.canScrollBackward ||
                    lazyListState.canScrollForward
                )
                    Box(modifier = Modifier.fillMaxWidth()) {
                        if (lazyListState.canScrollBackward)
                            EsLargeIcon(
                                res = EsIcons.ArrowLeft,
                                modifier = Modifier
                                    .clip(CircleShape)
                                    .pointerHoverIcon(PointerIcon.Hand)
                                    .clickable {
                                        scope.launch {
                                            lazyListState.animateScrollToItem(scrollPosition - 1)
                                        }
                                    }
                                    .padding(.5.spacing)
                                    .align(Alignment.CenterStart),
                            )

                        if (lazyListState.canScrollForward)
                            EsLargeIcon(
                                res = EsIcons.ArrowRight,
                                modifier = Modifier
                                    .clip(CircleShape)
                                    .pointerHoverIcon(PointerIcon.Hand)
                                    .clickable {
                                        scope.launch {
                                            lazyListState.animateScrollToItem(scrollPosition + 1)
                                        }
                                    }
                                    .padding(.5.spacing)
                                    .align(Alignment.CenterEnd),
                            )
                    }
            }

            Spacer(modifier = Modifier.height(1.spacing))
        }

        EsInput(
            value = value,
            onValueChange = { newValue ->
                onValueChange(newValue)
            },
            label = label,
            placeholder = "Type your message",
            message = message,
//            prefix = {
//                Row {
//                    EsLargeIcon(
//                        res = EsIcons.FilePicker,
//                        modifier = Modifier
//                            .clip(CircleShape)
//                            .pointerHoverIcon(PointerIcon.Hand)
//                            .clickable {
//                                pickerLauncher.launch()
//                                println("FilePicker")
//                            }
//                            .padding(.5.spacing),
//                    )
//
//                    EsLargeIcon(
//                        res = EsIcons.EmojiPicker,
//                        modifier = Modifier
//                            .clip(CircleShape)
//                            .pointerHoverIcon(PointerIcon.Hand)
//                            .clickable {
//                                println("EmojiPicker")
//                            }
//                            .padding(.5.spacing),
//                    )
//                }
//            },
            suffix = {
                EsLargeIcon(
                    res = EsIcons.Send,
                    modifier = Modifier
                        .clip(CircleShape)
                        .pointerHoverIcon(PointerIcon.Hand)
                        .clickable(
                            enabled = value.isNotEmpty() ||
                                filesModel.isNotEmpty(),
                        ) {
                            onValueChange("")
                            filesModel = emptyList()
                            onSend(value)
                        }
                        .padding(.5.spacing),
                )
            },
            showClearButton = showClearButton,
            enabled = enabled,
            readOnly = readOnly,
            state = state,
            visualTransformation = visualTransformation,
            keyboardOptions = keyboardOptions,
            keyboardActions = keyboardActions,
            singleLine = false,
            maxLines = Int.MAX_VALUE,
            minLines = minLines,
            interactionSource = interactionSource,
            cursorColor = cursorColor,
            minHeight = minHeight,
            shape = shape,
            innerPaddingValues = PaddingValues(top = 5.dp),
            modifier = modifier,
        )
    }
}